import type { ApiReturnDashboard } from '@highimpact-frontend/api-types';
import { CustomizedDialog } from '@highimpact/dashboard-ui';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SendIcon from '@mui/icons-material/Send';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { LegacyRef } from 'react';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDashboardHost } from '../context/DashboardHostContext';
import { useSubmit } from '../internal/hooks/useSubmit';

export type DashboardFormComponentProps = {
  readonly title: string;
  readonly isOpen: boolean;
  readonly onDashboardCreated: (info: {
    dashboardName: string;
    dashboard_id: number;
    site_id: number;
    project_id: number;
  }) => void;
  readonly onCancel: () => void;
  readonly project_id?: number;
  readonly site_id?: number;
};

const REQUIRED_NAME_ERROR_MESSAGE = 'Le nom est requis';

function DashboardFormComponent(
  {
    title,
    isOpen,
    onDashboardCreated,
    onCancel,
    project_id: projectIdProp,
    site_id: siteIdProp,
  }: DashboardFormComponentProps,
  _ref: LegacyRef<HTMLDivElement>
): JSX.Element {
  const host = useDashboardHost();
  const site_id = siteIdProp ?? host.site_id;
  const project_id = projectIdProp ?? host.project_id;

  const { t } = useTranslation('dashboards');

  const [name, setName] = useState('');
  const [hasError, setHasError] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState<string>(
    REQUIRED_NAME_ERROR_MESSAGE
  );
  const { submit, state } = useSubmit<void>();

  if (site_id === null || project_id === null) {
    return <div>Site ou projet inconnu</div>;
  }

  const handleNameInput: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (e) => {
    setName(e.target.value);
    if (e.target.value.trim().length > 0) {
      setHasError(false);
    }
  };

  const getIcon = () => {
    switch (state) {
      case 'Loading':
        return <CircularProgress color="inherit" size={20} />;
      case 'Success':
        return <CheckCircleIcon />;
      case 'Error':
        return <ErrorIcon />;
      default:
        return <SendIcon />;
    }
  };

  const handleSubmit = async () => {
    if (name.trim().length === 0) {
      setHasError(true);
      setNameErrorMessage(t(REQUIRED_NAME_ERROR_MESSAGE));
      return;
    }

    const dashboard: ApiReturnDashboard = {
      id: 0,
      name: name.trim(),
      elements: [],
      site_id,
      shared: false,
      type: 'user_dashboard' as const,
      masked: false,
      additional_properties: {
        cols: { lg: 24, md: 18, sm: 12, xs: 9, xxs: 6 },
        rowHeight: 16,
        margin: [6, 6],
      },
    };

    await submit({
      callbacks: [
        async () => {
          const created = await host.api.dashboard.postDashboard(dashboard);
          onDashboardCreated({
            dashboardName: created.name,
            dashboard_id: created.id!,
            site_id,
            project_id,
          });
          setName('');
        },
      ],
      errorMessages: {
        anyError: t(
          'Une erreur est survenue lors de la création du tableau de bord'
        ),
        unicityError: t('Ce nom de tableau de bord existe déjà pour ce site'),
      },
      successMessage: t(`Le tableau de bord a été créé`, { name: name }),
    });
  };

  return (
    <CustomizedDialog open={isOpen} onCancel={onCancel} title={title}>
      <Stack
        spacing={2}
        alignItems="center"
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        noValidate
      >
        <TextField
          error={hasError}
          helperText={hasError ? nameErrorMessage : undefined}
          label={t('Nom')}
          variant="outlined"
          fullWidth
          required
          value={name}
          placeholder={t('Mon tableau de bord')}
          onChange={handleNameInput}
        />

        <Button
          type="submit"
          variant="contained"
          startIcon={getIcon()}
          disabled={state === 'Loading'}
        >
          {state === 'Loading' ? t('En cours...') : t('Créer')}
        </Button>
      </Stack>
    </CustomizedDialog>
  );
}

export default forwardRef(DashboardFormComponent);
